/**
 * Deep progressive Sankey — the E2 detail's unified "what + when" explorer.
 *
 * Columns appear left→right as the user drills: [Total] → [End-use] → [Month] →
 * [Week] → [Day] → [Hour]. Each click expands the clicked node's children as the
 * next column, END-USE-SLICED (the focused path is filtered to one end-use all
 * the way down), so a user can trace e.g. `cooling → July → wk of Jul 13 → Tue →
 * 2 PM` and read the observed after-hours potential savings $ at every level.
 *
 * Presentation: floating white cards (thick colored left-border, layered type —
 * tiny uppercase eyebrow → bold title → mono metric — soft layered shadow +
 * inset highlight, hover-lift) over a translucent filled-ribbon SVG layer whose
 * band thickness carries the magnitude (∝ $). Each end-use's canonical color
 * (shared with the variance disaggregation viz) threads down its whole path.
 * HTML cards + one analytic SVG ribbon layer (no DOM measurement, no charting
 * dep); single-focus-path expansion keeps per-view nodes bounded.
 *
 * Selecting a week opens a week-level chart of the same expected-vs-actual
 * reading; picking a day within it swaps to the hourly one.
 *
 * Data: column 1 (end-uses) seeds from `drivers` (already in the main payload);
 * deeper levels lazy-fetch via `useSankeyDrill` (four fixed hook calls, one per
 * drill level, each disabled until its parent is chosen — rules-of-hooks safe).
 *
 * Copy contract: OBSERVED framing only — the figure is money already spent,
 * so no projected/forecast/will-reduce language.
 */
import type React from "react"
import { useState } from "react"

import { FONT_WEIGHT } from "@/models/typography"

import { Box, Stack, Typography, useTheme } from "@mui/material"

import { useSankeyDrill } from "../../data/use-sankey-drill"
import { Eyebrow } from "../../homepage/atoms"
import { enduseColor, enduseLabel } from "../variance-route/enduse-colors"
import { HourlyProfileChart } from "./hourly-profile-chart"
import type { E2Driver } from "./schedule-misalignment.types"
import { useFormatWasteCurrency } from "./use-format-waste-currency"
import { WeekProfileChart } from "./week-profile-chart"

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace"

interface NodeVM {
  key: string
  label: string
  dollars: number
  clickable: boolean
}

interface ColumnVM {
  title: string
  nodes: NodeVM[]
  focusedKey: string | null
  pending: boolean
  unavailable: boolean
  onClick?: (key: string) => void
}

// Layout (px). Cards are uniform height — magnitude is carried by the ribbon
// band thickness + each card's $, not by card size (mirrors a classic Sankey).
const COL_W = 220
const COL_GAP = 104
const HEADER_H = 22
const CARD_H = 66
const CARD_GAP = 16
const BAND_MAX = 74 // thickest ribbon (px) for a full-share child

const columnX = (c: number): number => c * (COL_W + COL_GAP)

interface LaidNode extends NodeVM {
  y: number // card top
  cy: number // card vertical center (ribbon anchor)
}

const layColumn = (nodes: NodeVM[]): LaidNode[] =>
  nodes.map((d, i) => {
    const y = HEADER_H + i * (CARD_H + CARD_GAP)
    return { ...d, y, cy: y + CARD_H / 2 }
  })

const columnHeight = (n: number): number =>
  HEADER_H + Math.max(n, 1) * (CARD_H + CARD_GAP)

// Filled Sankey ribbon: a closed top+bottom cubic band from a parent's right
// edge (centerline y0) to a child's left edge (centerline y1), `t` thick.
const ribbonPath = (
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: number
): string => {
  const cp = (x1 - x0) * 0.5
  const h = t / 2
  return [
    `M ${x0},${y0 - h}`,
    `C ${x0 + cp},${y0 - h} ${x1 - cp},${y1 - h} ${x1},${y1 - h}`,
    `L ${x1},${y1 + h}`,
    `C ${x1 - cp},${y1 + h} ${x0 + cp},${y0 + h} ${x0},${y0 + h}`,
    "Z",
  ].join(" ")
}

export const DeepSankey: React.FC<{
  siteId: string
  impactDollars: number
  drivers: E2Driver[]
}> = ({ siteId, impactDollars, drivers }) => {
  const theme = useTheme()
  const formatCurrency = useFormatWasteCurrency()

  // Focused expansion: one end-use, then [month, week] below it. Day is the
  // Sankey leaf — selecting a day opens the hourly chart, not a 6th column.
  const [enduse, setEnduse] = useState<string | null>(null)
  const [keys, setKeys] = useState<string[]>([])
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  // Three fixed drill hooks (rules-of-hooks): each disabled until its parent is
  // chosen. months uses parentKey "" (root); the rest use the chosen ancestor.
  const months = useSankeyDrill(siteId, enduse, enduse != null ? "" : null)
  const weeks = useSankeyDrill(siteId, enduse, keys[0] ?? null)
  const days = useSankeyDrill(siteId, enduse, keys[1] ?? null)

  const focusEnduse = (key: string) => {
    setEnduse(key)
    setKeys([])
    setSelectedDay(null)
  }
  const drillTo = (level: number, key: string) => {
    // level 0 = month, 1 = week
    setKeys((prev) => [...prev.slice(0, level), key])
    setSelectedDay(null)
  }

  const toNodes = (
    q: typeof months,
    level: number
  ): { nodes: NodeVM[]; pending: boolean; unavailable: boolean } => {
    if (q.isPending) {
      return { nodes: [], pending: true, unavailable: false }
    }
    const data = q.data
    if (!data?.available) {
      return { nodes: [], pending: false, unavailable: true }
    }
    return {
      nodes: (data.nodes ?? []).map((n) => ({
        key: n.key,
        label: n.label,
        dollars: n.dollars,
        clickable: level < 3 && n.has_children,
      })),
      pending: false,
      unavailable: false,
    }
  }

  // Assemble the visible columns.
  const columns: ColumnVM[] = []
  columns.push({
    title: "Total",
    nodes: [
      {
        key: "__total__",
        label: "Total",
        dollars: impactDollars,
        clickable: false,
      },
    ],
    focusedKey: "__total__",
    pending: false,
    unavailable: false,
  })
  columns.push({
    title: "End-use",
    nodes: [...drivers]
      .sort((a, b) => b.impact_dollars - a.impact_dollars)
      .map((d) => ({
        key: d.enduse,
        label: enduseLabel(d.enduse),
        dollars: d.impact_dollars,
        clickable: true,
      })),
    focusedKey: enduse,
    pending: false,
    unavailable: false,
    onClick: focusEnduse,
  })
  if (enduse != null) {
    const m = toNodes(months, 0)
    columns.push({
      title: "Month",
      nodes: m.nodes,
      focusedKey: keys[0] ?? null,
      pending: m.pending,
      unavailable: m.unavailable,
      onClick: (k) => {
        drillTo(0, k)
      },
    })
  }
  if (keys[0] != null) {
    const w = toNodes(weeks, 1)
    columns.push({
      title: "Week",
      nodes: w.nodes,
      focusedKey: keys[1] ?? null,
      pending: w.pending,
      unavailable: w.unavailable,
      onClick: (k) => {
        drillTo(1, k)
      },
    })
  }
  if (keys[1] != null) {
    const d = toNodes(days, 2)
    columns.push({
      title: "Day",
      // Day is the Sankey leaf — day cards are clickable to SELECT (opens the
      // hourly chart below), not to drill a further column.
      nodes: d.nodes.map((n) => ({ ...n, clickable: true })),
      focusedKey: selectedDay,
      pending: d.pending,
      unavailable: d.unavailable,
      onClick: (k) => {
        setSelectedDay(k)
      },
    })
  }

  // Breadcrumb: Total › Cooling › July › wk › day. Clicking an ancestor
  // collapses back to it.
  const crumbs: { label: string; onClick: () => void }[] = [
    {
      label: "Total",
      onClick: () => {
        setEnduse(null)
        setKeys([])
        setSelectedDay(null)
      },
    },
  ]
  if (enduse != null) {
    crumbs.push({
      label: enduseLabel(enduse),
      onClick: () => {
        setKeys([])
        setSelectedDay(null)
      },
    })
    keys.forEach((k, i) => {
      const col = columns[i + 2] // Month col is index 2
      const label = col?.nodes.find((n) => n.key === k)?.label ?? k
      crumbs.push({
        label,
        onClick: () => {
          setKeys((p) => p.slice(0, i + 1))
          setSelectedDay(null)
        },
      })
    })
  }

  // Layout.
  const laid = columns.map((c) => layColumn(c.nodes))
  const canvasH = columns.reduce(
    (mx, c) => Math.max(mx, columnHeight(c.nodes.length)),
    columnHeight(1)
  )
  const canvasW = columns.length * COL_W + (columns.length - 1) * COL_GAP

  const TOTAL_COLOR = theme.palette.grey[700]
  // Node color: Total = brand slate; End-use column = each end-use's canonical
  // color; every deeper (temporal) column inherits the FOCUSED end-use's color,
  // so a drilled path reads as one continuous colored thread.
  const colorForNode = (colIndex: number, key: string): string => {
    if (colIndex === 0) {
      return TOTAL_COLOR
    }
    if (colIndex === 1) {
      return enduseColor(key)
    }
    return enduse != null ? enduseColor(enduse) : TOTAL_COLOR
  }

  return (
    <Box>
      <Eyebrow>
        Where the after-hours potential savings came from, observed
      </Eyebrow>

      {/* Breadcrumb */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: "center", flexWrap: "wrap", mt: 0.5, mb: 1.5 }}
      >
        {crumbs.map((c, i) => (
          <Box
            key={`crumb-${c.label}`}
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            {i > 0 ? (
              <Typography
                color="text.secondary"
                sx={{ mx: 0.5 }}
                variant="body2"
              >
                ›
              </Typography>
            ) : null}
            {i === 1 && enduse != null ? (
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  bgcolor: enduseColor(enduse),
                  mr: 0.75,
                }}
              />
            ) : null}
            <Typography
              onClick={c.onClick}
              role="button"
              sx={{
                cursor: "pointer",
                fontWeight: i === crumbs.length - 1 ? "bold" : 500,
                color:
                  i === crumbs.length - 1 ? "text.primary" : "primary.main",
                "&:hover": { textDecoration: "underline" },
              }}
              tabIndex={0}
              variant="body2"
            >
              {c.label}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Surface — soft, faintly-tinted card the flow floats on; scrolls as
          columns accrue. */}
      <Box
        sx={{
          overflow: "auto",
          borderRadius: 3,
          border: 1,
          borderColor: "grey.200",
          bgcolor: "grey.25",
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(107,140,127,0.04) 0%, transparent 55%), radial-gradient(circle at 85% 90%, rgba(155,138,157,0.04) 0%, transparent 55%)",
          p: 3,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: canvasW,
            height: canvasH,
            flex: "none",
          }}
        >
          {/* Ribbon layer (behind cards). Explicit px width/height (1:1 with the
              card coordinate space) — no viewBox rescale, so ribbon endpoints
              land exactly on the card edges regardless of panel width. */}
          <Box
            component="svg"
            aria-hidden="true"
            height={canvasH}
            sx={{ position: "absolute", top: 0, left: 0, overflow: "visible" }}
            width={canvasW}
          >
            {columns.map((col, c) => {
              if (c === columns.length - 1) {
                return null
              }
              const childLaid = laid[c + 1]
              const parentLaid =
                c === 0
                  ? laid[0][0]
                  : laid[c].find((nn) => nn.key === col.focusedKey)
              if (!parentLaid || childLaid.length === 0) {
                return null
              }
              const childTotal = childLaid.reduce((s, n) => s + n.dollars, 0)
              const px = columnX(c) + COL_W
              const cx = columnX(c + 1)
              return childLaid.map((cn) => {
                const t =
                  childTotal > 0
                    ? Math.max(3, (cn.dollars / childTotal) * BAND_MAX)
                    : 3
                const color = colorForNode(c + 1, cn.key)
                const showPct = t >= 15 && childTotal > 0
                const pct = Math.round((cn.dollars / childTotal) * 100)
                const mx = (px + cx) / 2
                const my = (parentLaid.cy + cn.cy) / 2
                return (
                  <g key={`ribbon-${col.title}-${cn.key}`}>
                    <path
                      d={ribbonPath(px, parentLaid.cy, cx, cn.cy, t)}
                      fill={color}
                      opacity={0.2}
                    />
                    {showPct ? (
                      <text
                        fill="#fff"
                        fontFamily={MONO}
                        fontSize={11}
                        fontWeight={700}
                        style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
                        textAnchor="middle"
                        x={mx}
                        y={my + 4}
                      >
                        {pct}%
                      </text>
                    ) : null}
                  </g>
                )
              })
            })}
          </Box>

          {/* Column headers + node cards. */}
          {columns.map((col, c) => {
            const x = columnX(c)
            const colTotal = col.nodes.reduce((s, n) => s + n.dollars, 0)
            return (
              <Box key={col.title}>
                <Typography
                  sx={{
                    position: "absolute",
                    left: x,
                    top: 0,
                    width: COL_W,
                    fontSize: 9.5,
                    fontWeight: FONT_WEIGHT.bold,
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}
                >
                  {col.title}
                </Typography>

                {col.pending || col.unavailable ? (
                  <Typography
                    sx={{
                      position: "absolute",
                      left: x,
                      top: HEADER_H,
                      width: COL_W,
                      fontSize: 12,
                      color: "text.secondary",
                    }}
                  >
                    {col.pending ? "Loading…" : "No breakdown"}
                  </Typography>
                ) : null}

                {laid[c].map((node) => {
                  const isFocused = col.focusedKey === node.key
                  const clickable = node.clickable && col.onClick != null
                  const color = colorForNode(c, node.key)
                  const share =
                    c >= 1 && colTotal > 0
                      ? Math.round((node.dollars / colTotal) * 100)
                      : null
                  const activate = () => {
                    col.onClick?.(node.key)
                  }
                  return (
                    <Box
                      key={node.key}
                      aria-label={`${node.label}: ${formatCurrency(node.dollars)}`}
                      onClick={clickable ? activate : undefined}
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                activate()
                              }
                            }
                          : undefined
                      }
                      role={clickable ? "button" : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      sx={{
                        position: "absolute",
                        left: x,
                        top: node.y,
                        width: COL_W,
                        height: CARD_H,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: "2px",
                        px: 1.75,
                        py: 1,
                        overflow: "hidden",
                        bgcolor: "#fff",
                        borderRadius: "10px",
                        borderLeft: `5px solid ${color}`,
                        cursor: clickable ? "pointer" : "default",
                        boxShadow: isFocused
                          ? `0 10px 26px ${tintShadow(color, 0.28)}, inset 0 0 0 1px rgba(255,255,255,0.7)`
                          : "0 3px 12px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(255,255,255,0.5)",
                        transition:
                          "transform .2s cubic-bezier(.4,0,.2,1), box-shadow .2s cubic-bezier(.4,0,.2,1)",
                        ...(clickable && {
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow:
                              "0 12px 30px rgba(0,0,0,0.16), inset 0 0 0 1px rgba(255,255,255,0.85)",
                          },
                        }),
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 8.5,
                          fontWeight: FONT_WEIGHT.bold,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "grey.500",
                          lineHeight: 1,
                        }}
                      >
                        {col.title}
                        {share != null ? ` · ${share}%` : ""}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: FONT_WEIGHT.bold,
                          letterSpacing: "-0.2px",
                          color: "text.primary",
                          lineHeight: 1.15,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {node.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: MONO,
                          fontSize: 11,
                          fontWeight: 700,
                          color: "grey.600",
                          fontVariantNumeric: "tabular-nums",
                          lineHeight: 1,
                        }}
                      >
                        {formatCurrency(node.dollars)}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Day-select detail: hourly expected-vs-actual line chart. */}
      {enduse != null && selectedDay != null ? (
        <HourlyProfileChart day={selectedDay} enduse={enduse} siteId={siteId} />
      ) : null}

      {/* Week-select detail: the same reading one level up, until a day is
          picked. Its $ is the sum of the day charts' — same server-side
          builder — so drilling in never changes the number. */}
      {enduse != null && selectedDay == null && keys[1] != null ? (
        <WeekProfileChart enduse={enduse} siteId={siteId} week={keys[1]} />
      ) : null}
    </Box>
  )
}

// Colored soft shadow for a focused card (the node's own hue, low alpha).
function tintShadow(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) {
    return `rgba(0,0,0,${alpha})`
  }
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}
